/**
 * useGameBridge Hook
 * 
 * React hook for integrating the GameBridgeService with PlayPage.
 * Handles:
 * - Attaching/detaching from iframe
 * - Subscribing to stream events via BroadcastChannel
 * - Forwarding events to game in SDK-compatible format
 * - Managing SDK ready state
 * - Forwarding user data on auth changes
 * - Sending app preferences
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { gameBridgeService, SDKUserData, AppPreferences } from '../services/game/GameBridgeService';
import { appBridgeHandler } from '../services/game/AppBridgeHandler';
import { streamBroadcastService } from '../services/streaming/StreamBroadcastService';
import { BroadcastEvent } from '../types/streaming';
import { parseAndFormat } from '../lib/stream-parser';

// ============================================================================
// Types
// ============================================================================

export interface UseGameBridgeOptions {
  /** User data to send to game */
  user?: SDKUserData | null;
  /** App preferences to send to game */
  preferences?: AppPreferences;
  /** Callback when auth.promptSignIn is requested by game */
  onAuthPrompt?: () => void;
  /** Enable debug logging */
  debug?: boolean;
}

export interface UseGameBridgeReturn {
  /** Attach the bridge to an iframe */
  attachToIframe: (iframe: HTMLIFrameElement) => void;
  /** Detach the bridge from the current iframe */
  detach: () => void;
  /** Whether the SDK in the game is ready */
  isSDKReady: boolean;
  /** SDK version if ready */
  sdkVersion: string | null;
  /** Available SDK modules */
  sdkModules: string[];
  /** Whether bridge is attached to an iframe */
  isAttached: boolean;
  /** Send a custom event to the game */
  sendEvent: (type: string, data: any) => void;
  /** Send a test message to the game (debug only) */
  sendTestMessage: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useGameBridge(options: UseGameBridgeOptions = {}): UseGameBridgeReturn {
  const { user, preferences, onAuthPrompt, debug = false } = options;
  
  const [isSDKReady, setIsSDKReady] = useState(false);
  const [sdkVersion, setSdkVersion] = useState<string | null>(null);
  const [sdkModules, setSdkModules] = useState<string[]>([]);
  const [isAttached, setIsAttached] = useState(false);
  
  // Track previous user to detect changes
  const prevUserRef = useRef<SDKUserData | null | undefined>(undefined);
  
  // Track cleanup functions
  const cleanupRef = useRef<(() => void)[]>([]);

  /**
   * Log helper
   */
  const log = useCallback((...args: any[]) => {
    if (debug) {
      console.log('[useGameBridge]', ...args);
    }
  }, [debug]);

  /**
   * Attach to an iframe
   */
  const attachToIframe = useCallback((iframe: HTMLIFrameElement) => {
    log('Attaching to iframe...');
    
    // Initialize app bridge handler
    appBridgeHandler.initialize({
      onAuthPrompt,
      appVersion: '1.0.0',
    });
    
    // Attach game bridge service
    gameBridgeService.attach(iframe);
    setIsAttached(true);
    
    // Subscribe to SDK ready event
    const unsubSDKReady = gameBridgeService.onSDKReady(({ version, modules }) => {
      log('SDK ready! Version:', version, 'Modules:', modules);
      setIsSDKReady(true);
      setSdkVersion(version);
      setSdkModules(modules);
      
      // Send initial data now that SDK is ready
      if (user) {
        log('Sending initial user data:', user.username);
        gameBridgeService.sendUserData(user);
      }
      
      if (preferences) {
        log('Sending initial preferences');
        gameBridgeService.sendAppPreferences(preferences);
      }
    });
    cleanupRef.current.push(unsubSDKReady);
    
    // Subscribe to stream events from BroadcastChannel
    // Using shared stream-parser library for consistent formatting
    log('🔗 Subscribing to BroadcastChannel events...');
    const unsubBroadcast = streamBroadcastService.subscribe((event: BroadcastEvent) => {
      log('📥 Received broadcast event:', event.type);
      
      // Handle RAW stream events (NO MAPPING - direct to parser!)
      if (event.type === 'raw-stream-event') {
        try {
          const { rawEvent, platform } = event.payload as {
            eventType: 'comment' | 'gift' | 'like',  // Metadata only, not used
            rawEvent: any,
            platform: string
          };
          
          log('🔄 Processing raw event from', platform, '| Euler type:', rawEvent?.type);
          
          // Get SDK version for format compatibility
          const sdkVersion = gameBridgeService.getSDKVersion() || '2.1.5';
          
          // Handle stats events specially - they're already in SDK format
          if (rawEvent?.type === 'stats') {
            log('✅ Stats event (already formatted) | Sending to game...');
            gameBridgeService.sendRawEvent('stream-stats', rawEvent.data);
            log(`📤 Forwarded stream-stats to game from ${platform}`);
          } else {
            // Pass COMPLETE raw event to parser - NO modifications!
            // Parser will extract type from Euler's 'type' field (e.g. 'WebcastChatMessage')
            const sdkMessage = parseAndFormat(rawEvent, platform, sdkVersion);
            
            if (sdkMessage) {
              // Send directly to iframe with queueing support
              log('✅ Parsed to SDK format:', sdkMessage.type, `(SDK ${sdkVersion}) | Sending to game...`);
              gameBridgeService.sendRawEvent(sdkMessage.type, sdkMessage.data);
              log(`📤 Forwarded ${sdkMessage.type} to game from ${platform}`);
            } else {
              log('⚠️ parseAndFormat returned null for event');
            }
          }
        } catch (error) {
          console.warn('[GameBridge] Failed to parse stream event:', error);
        }
      }
      
      // Other events handled by UI (no forwarding needed)
      // stream-metrics, connection-state, room-info
    });
    cleanupRef.current.push(unsubBroadcast);
    
    log('Attached successfully');
  }, [user, preferences, onAuthPrompt, log]);

  /**
   * Detach from iframe
   */
  const detach = useCallback(() => {
    log('Detaching...');
    
    // Run all cleanup functions
    cleanupRef.current.forEach(cleanup => cleanup());
    cleanupRef.current = [];
    
    // Cleanup services
    appBridgeHandler.cleanup();
    gameBridgeService.detach();
    
    // Reset state
    setIsAttached(false);
    setIsSDKReady(false);
    setSdkVersion(null);
    setSdkModules([]);
    
    log('Detached');
  }, [log]);

  /**
   * Send a custom event to the game
   */
  const sendEvent = useCallback((type: string, data: any) => {
    gameBridgeService.sendRawEvent(type, data);
  }, []);

  /**
   * Effect: Forward user data when it changes
   */
  useEffect(() => {
    // Skip if not attached or SDK not ready
    if (!isAttached || !isSDKReady) {
      return;
    }
    
    // Skip if this is the initial render (handled in attachToIframe)
    if (prevUserRef.current === undefined) {
      prevUserRef.current = user;
      return;
    }
    
    // Check if user actually changed
    const prevUser = prevUserRef.current;
    const userChanged = user?.id !== prevUser?.id;
    
    if (userChanged) {
      if (user) {
        log('User changed, sending new user data:', user.username);
        gameBridgeService.sendUserData(user);
      } else {
        log('User signed out');
        gameBridgeService.sendUserSignedOut();
      }
    }
    
    prevUserRef.current = user;
  }, [user, isAttached, isSDKReady, log]);

  /**
   * Effect: Forward preferences when they change
   */
  useEffect(() => {
    if (isAttached && isSDKReady && preferences) {
      log('Preferences changed, sending update');
      gameBridgeService.sendAppPreferences(preferences);
    }
  }, [preferences, isAttached, isSDKReady, log]);

  /**
   * Effect: Update app bridge handler config when callbacks change
   */
  useEffect(() => {
    if (isAttached) {
      appBridgeHandler.updateConfig({
        onAuthPrompt,
      });
    }
  }, [onAuthPrompt, isAttached]);

  /**
   * Effect: Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (isAttached) {
        detach();
      }
    };
  }, []);

  return {
    attachToIframe,
    detach,
    isSDKReady,
    sdkVersion,
    sdkModules,
    isAttached,
    sendEvent,
    sendTestMessage: () => gameBridgeService.sendTestMessage(),
  };
}

export default useGameBridge;

